<template>
  <v-container class="py-6">
    <v-card class="elevation-12 rounded-xl overflow-hidden glass-card">
      <v-toolbar flat color="transparent" class="px-4 pt-4">
        <div class="d-flex align-center">
          <v-btn icon color="primary" class="mr-3 bg-primary-light" elevation="2">
            <v-icon>mdi-account-school</v-icon>
          </v-btn>
          <div>
            <h1 class="text-h5 font-weight-bold primary--text mb-0">
              {{ $t('extracurricular_activity_teacher') }}
            </h1>
            <span class="text-caption grey--text font-weight-medium">
              Gestión de asignaciones de maestros a talleres y deportes
            </span>
          </div>
        </div>
        <v-spacer></v-spacer>
        <v-btn
          color="primary"
          class="rounded-pill px-6 elevation-4 shadow-primary hover-scale transition-all"
          @click="openDialog()"
        >
          <v-icon left>mdi-plus-circle</v-icon>
          {{ $t('agregar') }}
        </v-btn>
      </v-toolbar>

      <v-card-text class="pa-6">
        <v-row class="mb-6">
          <v-col cols="12" sm="6" md="4">
            <v-text-field
              v-model="search"
              append-icon="mdi-magnify"
              :label="$t('Buscar')"
              single-line
              hide-details
              outlined
              dense
              class="rounded-lg bg-grey-lighten-4"
            ></v-text-field>
          </v-col>
        </v-row>

        <v-data-table
          :headers="headers"
          :items="mappedAssignments"
          :loading="loadingData"
          :search="search"
          :items-per-page="10"
          class="elevation-0 custom-table"
          :no-data-text="$t('no_resultados')"
          :footer-props="{
            'items-per-page-text': $t('filas_por_pagina'),
            'items-per-page-options': [5, 10, 20, 50]
          }"
        >
          <!-- Teacher Column -->
          <template v-slot:item.teacher_name="{ item }">
            <div class="d-flex align-center py-2">
              <v-avatar size="40" class="mr-3 elevation-2 border-2-white">
                <v-img :src="getTeacherAvatar(item.idTeacher) || defaultAvatar"></v-img>
              </v-avatar>
              <div>
                <span class="font-weight-bold text-subtitle-2 d-block">
                  {{ getTeacherName(item.idTeacher) }}
                </span>
              </div>
            </div>
          </template>

          <!-- Activity Column -->
          <template v-slot:item.activity_name="{ item }">
            <div class="d-flex align-center">
              <v-icon small :color="getActivityColor(item.idExtracurricularActivity)" class="mr-2">
                {{ getActivityIcon(item.idExtracurricularActivity) }}
              </v-icon>
              <span class="font-weight-medium">
                {{ getActivityName(item.idExtracurricularActivity) }}
              </span>
            </div>
          </template>

          <!-- Actions -->
          <template v-slot:item.actions="{ item }">
            <div class="d-flex justify-end">
              <v-tooltip bottom color="secondary">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    small
                    color="secondary"
                    class="mr-2 btn-action"
                    v-bind="attrs"
                    v-on="on"
                    @click="viewSchedule(item)"
                  >
                    <v-icon small>mdi-calendar-clock</v-icon>
                  </v-btn>
                </template>
                <span>{{ $t('schedules') }}</span>
              </v-tooltip>

              <v-tooltip bottom color="primary">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    small
                    color="primary"
                    class="mr-2 btn-action"
                    v-bind="attrs"
                    v-on="on"
                    @click="editAssignment(item)"
                  >
                    <v-icon small>mdi-pencil</v-icon>
                  </v-btn>
                </template>
                <span>{{ $t('modificar') }}</span>
              </v-tooltip>

              <v-tooltip bottom color="error">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    small
                    color="error"
                    class="btn-action"
                    v-bind="attrs"
                    v-on="on"
                    @click="confirmDelete(item)"
                  >
                    <v-icon small>mdi-trash-can-outline</v-icon>
                  </v-btn>
                </template>
                <span>{{ $t('delete') }}</span>
              </v-tooltip>
            </div>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <!-- Dialog Schedules -->
    <v-dialog v-model="dialogSchedules" max-width="500px">
      <v-card class="rounded-xl overflow-hidden glass-card shadow-2xl">
        <v-toolbar flat color="secondary" dark>
          <v-toolbar-title class="font-weight-bold">
            <v-icon left>mdi-calendar-clock</v-icon>
            {{ $t('schedules') }}: {{ selectedActivityName }}
          </v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon @click="dialogSchedules = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-card-text class="pa-6">
          <v-data-table
            :headers="headersSchedules"
            :items="schedules"
            :loading="loadingSchedules"
            hide-default-footer
            class="elevation-0 custom-table simple-table"
            :no-data-text="$t('no_registros')"
          >
            <template v-slot:item.dayWeek="{ item }">
              <span class="font-weight-bold">{{ getDayName(item.dayWeek) }}</span>
            </template>
            <template v-slot:item.time_range="{ item }">
              <v-chip outined small color="secondary" class="font-weight-medium">
                <v-icon left x-small>mdi-clock-outline</v-icon>
                {{ item.starTime.substring(0, 5) }} - {{ item.endTime.substring(0, 5) }}
              </v-chip>
            </template>
          </v-data-table>
        </v-card-text>

        <v-card-actions class="pa-6 pt-0">
          <v-spacer></v-spacer>
          <v-btn color="primary" text class="rounded-pill px-6" @click="dialogSchedules = false">
            {{ $t('cerrar') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog Add/Edit -->
    <v-dialog v-model="dialog" max-width="550px" persistent transition="dialog-bottom-transition">
      <v-card class="rounded-xl overflow-hidden glass-card shadow-2xl">
        <v-toolbar flat color="primary" dark>
          <v-toolbar-title class="font-weight-bold">
            {{ editedIndex === -1 ? $t('assign_teacher_activity') : $t('modificar') }}
          </v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon @click="close">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-card-text class="pa-6">
          <v-form ref="form" v-model="valid">
            <v-row>
              <v-col cols="12" class="pb-1">
                <span class="text-subtitle-2 font-weight-bold black--text mb-2 d-inline-block">
                  {{ $t('teacher') }}
                </span>
                <v-autocomplete
                  v-model="editedItem.idTeacher"
                  :items="teachers"
                  :loading="loadingTeachers"
                  :item-text="teacherText"
                  item-value="id"
                  :label="$t('placeholder_choose_teacher')"
                  outlined
                  dense
                  class="rounded-lg custom-input"
                  prepend-inner-icon="mdi-account-tie"
                  :rules="[
                    v => !!v || $t('required'),
                    v => !assignments.some(a => a.idTeacher === v && a.idExtracurricularActivity === editedItem.idExtracurricularActivity && a.idExtracurricularActivityTeacher !== editedItem.idExtracurricularActivityTeacher) || $t('msg_duplicate_assignment')
                  ]"
                  :no-data-text="$t('no_resultados')"
                  color="primary"
                >
                  <template v-slot:item="data">
                    <v-list-item-avatar>
                      <img :src="data.item.idUser.avatar || defaultAvatar">
                    </v-list-item-avatar>
                    <v-list-item-content>
                      <v-list-item-title>
                        {{ data.item.idUser.first_name }} {{ data.item.idUser.last_name }}
                      </v-list-item-title>
                    </v-list-item-content>
                  </template>
                </v-autocomplete>
              </v-col>

              <v-col cols="12" class="pt-2">
                <span class="text-subtitle-2 font-weight-bold black--text mb-2 d-inline-block">
                  {{ $t('extracurricular_activities') }}
                </span>
                <v-autocomplete
                  v-model="editedItem.idExtracurricularActivity"
                  :items="activities"
                  :loading="loadingActivities"
                  item-text="name"
                  item-value="idExtracurricularActivity"
                  :label="$t('placeholder_choose_extracurricular_activity')"
                  outlined
                  dense
                  class="rounded-lg custom-input"
                  prepend-inner-icon="mdi-star"
                  :rules="[
                    v => !!v || $t('required'),
                    v => !assignments.some(a => a.idExtracurricularActivity === v && a.idTeacher === editedItem.idTeacher && a.idExtracurricularActivityTeacher !== editedItem.idExtracurricularActivityTeacher) || $t('msg_duplicate_assignment')
                  ]"
                  :no-data-text="$t('no_resultados')"
                  color="primary"
                >
                  <template v-slot:item="data">
                    <v-list-item-icon>
                      <v-icon :color="getActivityColor(data.item.idExtracurricularActivity)">
                        {{ getActivityIcon(data.item.idExtracurricularActivity) }}
                      </v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title>{{ data.item.name }}</v-list-item-title>
                      <v-list-item-subtitle>
                        {{ getBaseActivityName(data.item.idActivity) }}
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </template>
                </v-autocomplete>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>

        <v-divider class="mx-6 opacity-30"></v-divider>

        <v-card-actions class="pa-6 pt-4">
          <v-spacer></v-spacer>
          <v-btn text class="rounded-pill px-6 grey--text text--darken-2" @click="close">
            {{ $t('cerrar') }}
          </v-btn>
          <v-btn
            color="primary"
            class="rounded-pill px-8 elevation-4 shadow-primary hover-scale transition-all"
            @click="save"
            :loading="saving"
            :disabled="!valid"
          >
            {{ $t('guardar') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Confirm Delete Dialog -->
    <v-dialog v-model="dialogDelete" max-width="450px" transition="dialog-center-transition">
      <v-card class="rounded-xl overflow-hidden glass-card p-4">
        <v-card-title class="headline d-flex align-center pb-2">
          <v-icon color="error" large class="mr-3">mdi-alert-circle-outline</v-icon>
          <span class="font-weight-bold">{{ $t('aviso') }}</span>
        </v-card-title>

        <v-card-text class="pt-4 text-body-1 dark--text font-weight-medium">
          {{ $t('msg_delete_activity_teacher') }}
          <div v-if="itemToDelete" class="mt-4 pa-4 bg-grey-lighten-4 rounded-lg border-l-4-error">
            <div class="d-flex align-center mb-2">
              <v-icon x-small color="grey" class="mr-2">mdi-account</v-icon>
              <span class="text-caption font-weight-bold grey--text text-uppercase tracking-wider">Maestro</span>
            </div>
            <div class="font-weight-bold primary--text mb-3">
              {{ getTeacherName(itemToDelete.idTeacher) }}
            </div>
            
            <div class="d-flex align-center mb-2">
              <v-icon x-small color="grey" class="mr-2">mdi-star</v-icon>
              <span class="text-caption font-weight-bold grey--text text-uppercase tracking-wider">Actividad</span>
            </div>
            <div class="font-weight-bold secondary--text">
              {{ getActivityName(itemToDelete.idExtracurricularActivity) }}
            </div>
          </div>
        </v-card-text>

        <v-card-actions class="pa-6">
          <v-spacer></v-spacer>
          <v-btn text class="rounded-pill px-6" @click="dialogDelete = false">
            No
          </v-btn>
          <v-btn
            color="error"
            class="rounded-pill px-8 elevation-4 shadow-error hover-scale transition-all"
            @click="deleteItemConfirm"
            :loading="deleting"
          >
            {{ $t('Si') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { HTTP } from '@/main.js'
import store from '@/store'
import { mapGetters } from 'vuex'

export default {
  name: 'ExtracurricularActivityTeacher',
  data: () => ({
    search: '',
    valid: true,
    dialog: false,
    dialogDelete: false,
    loadingData: false,
    loadingTeachers: false,
    loadingActivities: false,
    saving: false,
    deleting: false,
    dialogSchedules: false,
    loadingSchedules: false,
    schedules: [],
    selectedActivityName: '',

    teachers: [],
    activities: [],
    baseActivities: [],
    assignments: [],
    
    editedIndex: -1,
    editedItem: {
      idExtracurricularActivityTeacher: null,
      idTeacher: null,
      idExtracurricularActivity: null
    },
    defaultItem: {
      idExtracurricularActivityTeacher: null,
      idTeacher: null,
      idExtracurricularActivity: null
    },
    itemToDelete: null,
    
    days: [
      { name: 'Lunes', value: 0 }, { name: 'Martes', value: 1 }, { name: 'Miércoles', value: 2 },
      { name: 'Jueves', value: 3 }, { name: 'Viernes', value: 4 }, { name: 'Sábado', value: 5 },
      { name: 'Domingo', value: 6 }
    ],

    defaultAvatar: 'https://cdn.pixabay.com/photo/2015/10/05/22/37/blank-profile-picture-973460_1280.png'
  }),

  computed: {
    ...mapGetters(['getProfile']),
    headers() {
      return [
        { text: this.$t('teacher'), value: 'teacher_name', align: 'start' },
        { text: this.$t('extracurricular_activities'), value: 'activity_name', align: 'start' },
        { text: this.$t('acciones'), value: 'actions', sortable: false, align: 'end' }
      ]
    },
    headersSchedules() {
      return [
        { text: this.$t('day'), value: 'dayWeek', align: 'start' },
        { text: this.$t('schedule'), value: 'time_range', align: 'center', sortable: false }
      ]
    },
    mappedAssignments() {
      return this.assignments.map(item => ({
        ...item,
        teacher_name: this.getTeacherName(item.idTeacher),
        activity_name: this.getActivityName(item.idExtracurricularActivity)
      }))
    }
  },

  created() {
    this.init()
  },

  methods: {
    async init() {
      this.loadingData = true
      try {
        await Promise.all([
          this.getTeachers(),
          this.getActivities(),
          this.getBaseActivities(),
          this.getAssignments()
        ])
      } catch (error) {
        console.error('Error loading data:', error)
      } finally {
        this.loadingData = false
      }
    },

    getTeachers() {
      this.loadingTeachers = true
      return HTTP.get('teacher/', {
        headers: { 'authorization': 'Token ' + store.getters.getToken }
      }).then(res => {
        this.teachers = res.data
      }).finally(() => { this.loadingTeachers = false })
    },

    getActivities() {
      this.loadingActivities = true
      return HTTP.get('extracurricular_activities_by_school/', {
        headers: { 'authorization': 'Token ' + store.getters.getToken }
      }).then(res => {
        this.activities = res.data
      }).finally(() => { this.loadingActivities = false })
    },

    getBaseActivities() {
      return HTTP.get('activities_by_school/', {
        headers: { 'authorization': 'Token ' + store.getters.getToken }
      }).then(res => {
        this.baseActivities = res.data
      })
    },

    getAssignments() {
      // Assuming this endpoint exists based on the pattern in activity.vue
      return HTTP.get('/extracurricular_activity_by_teacher/', {
        headers: { 'authorization': 'Token ' + store.getters.getToken }
      }).then(res => {
        this.assignments = res.data
      }).catch(() => {
        // Fallback or handle if endpoint is slightly different
        // Re-attempt with singular if plural fails
        return HTTP.get('/extracurricular_activity_by_teacher/', {
           headers: { 'authorization': 'Token ' + store.getters.getToken }
        }).then(res => {
           this.assignments = res.data
        })
      })
    },

    teacherText(item) {
      if (!item.idUser) return 'Unknown'
      return `${item.idUser.first_name} ${item.idUser.last_name}`
    },

    getTeacherAvatar(idTeacher) {
      const teacher = this.teachers.find(t => t.id === idTeacher)
      return teacher && teacher.idUser ? teacher.idUser.avatar : null
    },

    getTeacherName(idTeacher) {
      const teacher = this.teachers.find(t => t.id === idTeacher)
      if (!teacher || !teacher.idUser) return 'Cargando...'
      return `${teacher.idUser.first_name} ${teacher.idUser.last_name}`
    },

    getActivityName(idActivity) {
      const activity = this.activities.find(a => a.idExtracurricularActivity === idActivity)
      return activity ? activity.name : 'Cargando...'
    },

    getBaseActivityName(idBaseActivity) {
      const base = this.baseActivities.find(b => b.idActivity === idBaseActivity)
      return base ? base.name : ''
    },

    getActivityIcon(idActivity) {
      const activity = this.activities.find(a => a.idExtracurricularActivity === idActivity)
      if (!activity) return 'mdi-star'
      const base = this.baseActivities.find(b => b.idActivity === activity.idActivity)
      if (!base) return 'mdi-star'
      return base.workshop ? 'mdi-palette' : 'mdi-run-fast'
    },

    getActivityColor(idActivity) {
      const activity = this.activities.find(a => a.idExtracurricularActivity === idActivity)
      if (!activity) return 'grey'
      const base = this.baseActivities.find(b => b.idActivity === activity.idActivity)
      if (!base) return 'grey'
      return base.workshop ? 'success' : 'info'
    },

    openDialog() {
      this.editedIndex = -1
      this.editedItem = Object.assign({}, this.defaultItem)
      this.dialog = true
      this.$nextTick(() => { this.$refs.form.resetValidation() })
    },

    viewSchedule(item) {
      this.selectedActivityName = this.getActivityName(item.idExtracurricularActivity)
      this.getSchedules(item.idExtracurricularActivity)
      this.dialogSchedules = true
    },

    getSchedules(idActivity) {
      this.loadingSchedules = true
      HTTP.get(`/extracurricular_activity_schedules_by_activity/${idActivity}/`, {
        headers: { 'authorization': 'Token ' + store.getters.getToken }
      }).then(res => {
        this.schedules = res.data
      }).finally(() => { this.loadingSchedules = false })
    },

    getDayName(value) {
      const day = this.days.find(d => d.value === value)
      return day ? day.name : ''
    },

    editAssignment(item) {
      this.editedIndex = this.assignments.indexOf(item)
      this.editedItem = Object.assign({}, item)
      this.dialog = true
    },

    confirmDelete(item) {
      this.itemToDelete = item
      this.dialogDelete = true
    },

    close() {
      this.dialog = false
      this.$nextTick(() => {
        this.editedItem = Object.assign({}, this.defaultItem)
        this.editedIndex = -1
      })
    },

    save() {
      if (!this.$refs.form.validate()) return

      this.saving = true
      const method = this.editedIndex > -1 ? 'put' : 'post'
      // Try plural endpoint, if it fails backend likely uses singular
      const baseUrl = '/extracurricular_activity_by_teacher/'
      const id = this.editedItem.idExtracurricularActivityTeacher
      const url = this.editedIndex > -1 ? `${baseUrl}${id}/` : baseUrl

      HTTP({
        method, url, data: this.editedItem,
        headers: { 'authorization': 'Token ' + store.getters.getToken }
      }).then(() => {
        this.showSnackbar(this.editedIndex > -1 ? this.$t('msg_activity_teacher_updated') : this.$t('msg_activity_teacher_added'), 'success')
        this.getAssignments()
        this.close()
      }).catch(() => {
        // Retry with singular if plural might be wrong (backup)
        const altBaseUrl = 'extracurricular_activity_by_teacher/'
        const altUrl = this.editedIndex > -1 ? `${altBaseUrl}${id}/` : altBaseUrl
        HTTP({
           method, url: altUrl, data: this.editedItem,
           headers: { 'authorization': 'Token ' + store.getters.getToken }
        }).then(() => {
           this.showSnackbar(this.editedIndex > -1 ? this.$t('msg_activity_teacher_updated') : this.$t('msg_activity_teacher_added'), 'success')
           this.getAssignments()
           this.close()
        })
      }).finally(() => { this.saving = false })
    },

    deleteItemConfirm() {
      this.deleting = true
      const id = this.itemToDelete.idExtracurricularActivityByTeacher
      
      HTTP.delete(`extracurricular_activity_by_teacher/${id}/`, {
        headers: { 'authorization': 'Token ' + store.getters.getToken }
      }).then(() => {
        this.showDeleteSuccess()
      }).catch(() => {
        // Retry with singular
        HTTP.delete(`extracurricular_activity_by_teacher/${id}/`, {
           headers: { 'authorization': 'Token ' + store.getters.getToken }
        }).then(() => {
           this.showDeleteSuccess()
        })
      }).finally(() => { this.deleting = false })
    },

    showDeleteSuccess() {
      this.showSnackbar(this.$t('msg_activity_teacher_deleted'), 'success')
      this.getAssignments()
      this.dialogDelete = false
    },

    clear() {
      this.$store.commit('snackbar_activate_false')
    },

    showSnackbar(message, color) {
      this.$store.commit('snackbar_change_message', message)
      this.$store.commit('snackbar_change_color', color)
      this.$store.commit('snackbar_activate_true')
      setTimeout(this.clear, 3000)
    }
  }
}
</script>

<style scoped>
.glass-card {
  background: rgba(255, 255, 255, 0.9) !important;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.bg-primary-light {
  background-color: rgba(var(--v-primary-base), 0.1) !important;
}

.bg-grey-lighten-4 {
  background-color: #f8f9fa !important;
}

.border-2-white {
  border: 2px solid white;
}

.shadow-primary {
  box-shadow: 0 4px 14px 0 rgba(var(--v-primary-base), 0.39) !important;
}

.shadow-error {
  box-shadow: 0 4px 14px 0 rgba(255, 82, 82, 0.39) !important;
}

.shadow-2xl {
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25) !important;
}

.hover-scale:hover {
  transform: scale(1.02);
}

.transition-all {
  transition: all 0.3s ease;
}

.custom-table >>> thead th {
  background-color: #f8f9fa !important;
  color: #5f6368 !important;
  font-weight: 700 !important;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-size: 0.75rem !important;
  height: 56px !important;
}

.custom-table >>> tbody tr:hover {
  background-color: rgba(var(--v-primary-base), 0.02) !important;
}

.btn-action {
  transition: all 0.2s ease;
}

.btn-action:hover {
  background-color: rgba(var(--v-primary-base), 0.1) !important;
  transform: translateY(-2px);
}

.custom-input >>> .v-input__slot {
  box-shadow: none !important;
}

.border-l-4-error {
  border-left: 4px solid #ff5252;
}

.tracking-wider {
  letter-spacing: 1px;
}
</style>
